import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { useAuth } from '../hooks/use-auth'
import { AuthFunctions, hasPermission } from '../utils/permissions'
import { AppRoutes } from './Routes'
import { generatePath, useNavigate } from 'react-router'
import { PAGE_MARGIN_TOP, PAGE_MARGIN_X } from './masterComponents/layout'
import { StartHome } from '../assets/svgs/StartHome'
import { FileList3LineIcon } from '../assets/svgs/FileList3LineIcon'
import { ListOrderedIcon } from '../assets/svgs/ListOrderedIcon'
import { UserSettingsIcon } from '../assets/svgs/UserSettingsIcon'
import React, { useEffect } from 'react'
import { GetOrganizationsForUser } from '../services/OrganizationService'
import { ActionCard } from './ActionCard'
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined'
import { LoginPage } from './customComponents/Login'

export function HomePage (): JSX.Element {
  const auth = useAuth()
  const nav = useNavigate()
  const [hasMultipleOrgs, setHasMultipleOrgs] = React.useState(false)

  useEffect(() => {
    const loadUserOrganizations = async (): Promise<void> => {
      const userOrgs = await GetOrganizationsForUser()
      setHasMultipleOrgs(userOrgs.length > 1)
    }

    void loadUserOrganizations()
  }, [])

  if (auth.loading) {
    return <CircularProgress />
  }

  const hasCETAAccess = auth.user != null && hasPermission(auth.user, AuthFunctions.CETA_PARTICIPATION) && !auth.user.isOrgOnboarding

  const hasAnnualPlanAccess = auth.user != null && (hasPermission(auth.user, AuthFunctions.ANNUAL_PLANNING_REVIEW) ||
      hasPermission(auth.user, AuthFunctions.ANNUAL_PLANNING_REVIEW_EDIT) ||
      hasPermission(auth.user, AuthFunctions.ANNUAL_PLANNING_ADMINISTRATION) ||
      hasPermission(auth.user, AuthFunctions.ANNUAL_PLANNING_ADMINISTRATION_EDIT) ||
      hasPermission(auth.user, AuthFunctions.ANNUAL_PLANNING_CONTRIBUTION) ||
      hasPermission(auth.user, AuthFunctions.ANNUAL_PLANNING_CONTRIBUTION_EDIT))

  const hasAdminAccess = auth.user != null && (hasPermission(auth.user, AuthFunctions.PROJECT_SETTINGS) ||
      hasPermission(auth.user, AuthFunctions.RCN_MANAGEMENT) ||
      hasPermission(auth.user, AuthFunctions.ISD_MANAGEMENT) ||
      hasPermission(auth.user, AuthFunctions.DISTRICT_MANAGEMENT) ||
      hasPermission(auth.user, AuthFunctions.RCN_ADMINISTRATION) ||
      hasPermission(auth.user, AuthFunctions.ISD_ADMINISTRATION) ||
      hasPermission(auth.user, AuthFunctions.DISTRICT_ADMINISTRATION))

  return (<Box>
    <StartHome
      style={{
        position: 'relative',
        width: '100vw',
        height: 'fit-content',
        left: `-${PAGE_MARGIN_X / 2}vw`,
        top: `-${PAGE_MARGIN_TOP}vh`,
        marginLeft: `-${PAGE_MARGIN_X / 2}vw`,
        marginRight: `-${PAGE_MARGIN_X / 2}vw`
      }}
    />

    {auth.user == null && <LoginPage />}

    {auth.user != null && <>
      {auth.user.isActive && !auth.user.noAccount &&
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Box>
            <Typography variant='h1' fontSize='32px' mb='.5em'>Welcome to the START Planning and Data System (PDS)!</Typography>
            <Typography variant='body2' fontSize='16px'>The START PDS offers school staff a collection of tools to promote the use of evidence-based practices for students with autism and related disabilities. You can access the START PDS using the cards below.</Typography>
          </Box>
          {hasMultipleOrgs && <Typography data-testid='multipleOrgsText' variant='body1'>Your account is connected to multiple organizations in the START PDS such as an RCN, ISD, or district. Ensure you are viewing the system for the correct organization by clicking the dropdown at the top left of the screen.</Typography>}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
            {hasAnnualPlanAccess && <ActionCard
              dataTestId='annualPlanAccessCard'
              backgroundColor='var(--blue-50)'
              iconBackgroundColor='var(--blue-500)'
              icon={<FileList3LineIcon />}
              text='Annual Plan'
              subtext='Identify and address local needs in key priority areas set by START.'
              buttonText='Access Annual Plan'
              handleButtonClick={() => { nav(AppRoutes.PLANNING) }}
            />}
            {hasAnnualPlanAccess && <ActionCard
              dataTestId='annualPlanImplementationAccessCard'
              backgroundColor='var(--light-blue-50)'
              iconBackgroundColor='var(--light-blue-500)'
              icon={<FactCheckIcon sx={{ fill: 'white', width: '64px', height: '64px' }} />}
              text='Implementation'
              subtext='Update and track your priority activities for your RCN Contract.'
              buttonText='Access Implementation'
              handleButtonClick={() => { nav(AppRoutes.IMPLEMENTATION) }}
            />}
            {hasCETAAccess && <ActionCard
              dataTestId='cetaAccessCard'
              backgroundColor='var(--purple-50)'
              iconBackgroundColor='var(--purple-500)'
              icon={<ListOrderedIcon />}
              text='CETA'
              subtext='Enhance the teaching environment for all students, especially those with higher support needs.'
              buttonText='Access CETA'
              handleButtonClick={() => { nav(AppRoutes.IMPLEMENTATION) }}
            />}
            {hasAdminAccess && <ActionCard
              dataTestId='adminAccessCard'
              backgroundColor='var(--green-50)'
              iconBackgroundColor='var(--green-600)'
              icon={<UserSettingsIcon />}
              text='Admin'
              subtext='Access your START PDS administrative permissions for your organization.'
              buttonText='Access Admin'
              handleButtonClick={() => {
                const adminRoute = generatePath(AppRoutes.ADMINISTRATION, { id: auth.user?.currentOrgId.toString() ?? '' })
                nav(adminRoute)
              }}
            />}
          </Box>
        </Box>
        }

      {(auth.user.noAccount || !auth.user.isActive) &&
        <Box>
          <Typography variant='body1' sx={{ color: 'red', mt: '1.25em' }}>
            You have successfully logged in, but your account is not authorized for START.
          </Typography>

          <Button
            variant='contained'
            color='primary'
            onClick={auth.logout}
            sx={{ mt: '1.25em', textTransform: 'none' }}
          >
            Logout
          </Button>
        </Box>
        }
    </>}
  </Box>)
}
